/**
 * "Demandes d'ouverture de compte" — the ADV's list.
 *
 * It opens on the requests that still need a person, and that is the screen's
 * whole reason: a distributor's decided requests accumulate for years, and a
 * list that opened on them would put last year's refusals above this
 * morning's applicant. The filter can show everything, and the count of what
 * is open stays visible when it does — otherwise turning the filter off
 * quietly hides how much work there is.
 *
 * The ICE is flagged and never used to refuse. The design's own list carries a
 * request with no ICE at all and another refused *by a person* for an invalid
 * one, which is exactly the distinction: the number is transcribed from a
 * document by somebody who may have it at an angle.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { EM_DASH, day } from "@hardauto/core-i18n";
import {
  Badge,
  Checkbox,
  DataTable,
  Input,
  Page,
  Toolbar,
  type BadgeTone,
  type Column,
} from "@hardauto/core-ui";

import { OnboardingApi, type RequestRow, type RequestStatus } from "../api";
import { NAMESPACE } from "../i18n";
import { ONBOARDING_PATHS } from "../paths";
import "./onboarding.css";

/**
 * How a status reads at a glance.
 *
 * `incomplete` is a warning and not a danger: it is a request waiting for the
 * applicant, and a red badge would put it beside the refusals.
 */
const TONE: Record<RequestStatus, BadgeTone> = {
  submitted: "info",
  under_review: "neutral",
  incomplete: "warning",
  approved: "success",
  rejected: "neutral",
};

export function RequestsScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new OnboardingApi(client), [client]);

  const [term, setTerm] = useState("");
  const [openOnly, setOpenOnly] = useState(true);
  const search = useDebounced(term, 250);

  const fetcher = useCallback(
    () => api.list({ open_only: openOnly, ...(search ? { search } : {}), limit: 100 }),
    [api, openOnly, search],
  );
  const requests = useResource(fetcher);

  const columns: Column<RequestRow>[] = [
    {
      key: "received",
      header: t("list.column.received"),
      render: (row) => day(row.created_at, i18n.language),
    },
    {
      key: "company",
      header: t("list.column.company"),
      render: (row) => (
        <div>
          <Link to={ONBOARDING_PATHS.request} params={{ id: row.id }}>
            {row.company_name}
          </Link>
          <div className="hardauto-code">{row.reference}</div>
        </div>
      ),
    },
    {
      key: "taxId",
      header: t("list.column.taxId"),
      render: (row) =>
        row.tax_id ? (
          <span>
            <code className="hardauto-code">{row.tax_id}</code>
            {row.ice_looks_valid ? null : (
              <>
                {" "}
                <Badge tone="warning">{t("list.iceInvalid")}</Badge>
              </>
            )}
          </span>
        ) : (
          EM_DASH
        ),
    },
    { key: "city", header: t("list.column.city"), render: (row) => row.city || EM_DASH },
    {
      key: "documents",
      header: t("list.column.documents"),
      render: (row) => <code className="hardauto-code">{row.documents_summary}</code>,
    },
    {
      key: "rep",
      header: t("list.column.rep"),
      render: (row) => row.sales_rep_code || EM_DASH,
    },
    {
      key: "status",
      header: t("list.column.status"),
      render: (row) => <Badge tone={TONE[row.status]}>{t(`status.${row.status}`)}</Badge>,
    },
  ];

  return (
    <Page
      title={t("list.title")}
      description={t("list.intro")}
      eyebrow={
        requests.data ? t("list.openCount", { count: requests.data.open_count }) : t("list.eyebrow")
      }
    >
      <Toolbar>
        <Input
          type="search"
          aria-label={t("list.search")}
          placeholder={t("list.search")}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        <Checkbox
          label={t("list.openOnly")}
          checked={openOnly}
          onChange={(event) => setOpenOnly(event.target.checked)}
        />
      </Toolbar>

      <DataTable<RequestRow>
        caption={t("list.title")}
        columns={columns}
        rows={requests.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={requests.loading}
        loadingLabel={t("list.title")}
        error={
          requests.failed
            ? { title: t("error.load"), onRetry: requests.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{ title: openOnly ? t("list.empty") : t("list.emptyAll") }}
      />
    </Page>
  );
}
